import { supabase } from '../lib/supabase';

/**
 * Service for system status monitoring and configuration audit trail
 */
export const systemMonitoringService = {
  /**
   * Get current system status
   * @returns {Promise<Object>} System status with metrics
   */
  async getSystemStatus() {
    try {
      const { data, error } = await supabase?.from('system_status')?.select('*')?.order('last_checked_at', { ascending: false })?.limit(1)?.single();

      if (error) throw error;

      if (!data) {
        return {
          status: 'operational',
          activeUsers: 0,
          databaseSize: '0 GB',
          storageUsed: '0 GB',
          uptime: '0%',
          lastUpdated: new Date()
        };
      }

      // Convert snake_case to camelCase for React
      return {
        status: data?.status,
        activeUsers: data?.active_users,
        databaseSize: `${(data?.database_size_mb / 1024)?.toFixed(1)} GB`,
        storageUsed: `${(data?.storage_used_mb / 1024)?.toFixed(1)} GB`,
        uptime: `${data?.uptime_percentage}%`,
        lastUpdated: new Date(data.last_checked_at)
      };
    } catch (error) {
      console.error('Error fetching system status:', error);
      throw error;
    }
  },

  /**
   * Update system status (superadmin only)
   * @param {Object} statusData - Status data to update
   * @returns {Promise<Object>} Updated status
   */
  async updateSystemStatus(statusData) {
    try {
      // Get current status ID
      const { data: currentStatus } = await supabase?.from('system_status')?.select('id')?.order('last_checked_at', { ascending: false })?.limit(1)?.single();

      if (!currentStatus) {
        throw new Error('No system status record found');
      }

      // Convert camelCase to snake_case for database
      const updateData = {
        status: statusData?.status,
        active_users: statusData?.activeUsers,
        database_size_mb: statusData?.databaseSizeMb,
        storage_used_mb: statusData?.storageUsedMb,
        uptime_percentage: statusData?.uptimePercentage,
        last_checked_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase?.from('system_status')?.update(updateData)?.eq('id', currentStatus?.id)?.select()?.single();

      if (error) throw error;

      return {
        status: data?.status,
        activeUsers: data?.active_users,
        databaseSize: `${(data?.database_size_mb / 1024)?.toFixed(1)} GB`,
        storageUsed: `${(data?.storage_used_mb / 1024)?.toFixed(1)} GB`,
        uptime: `${data?.uptime_percentage}%`,
        lastUpdated: new Date(data.last_checked_at)
      };
    } catch (error) {
      console.error('Error updating system status:', error);
      throw error;
    }
  },

  /**
   * Get configuration audit trail
   * @param {Object} options - Query options (limit, offset)
   * @returns {Promise<Array>} Audit trail entries
   */
  async getAuditTrail(options = {}) {
    try {
      const { limit = 50, offset = 0 } = options;

      let query = supabase?.from('configuration_audit_trail')?.select(`
          id,
          action,
          description,
          user_id,
          metadata,
          created_at,
          user_profiles (
            email,
            full_name
          )
        `)?.order('created_at', { ascending: false })?.range(offset, offset + limit - 1);

      const { data, error } = await query;

      if (error) throw error;

      // Convert to camelCase and format for React
      return data?.map(entry => ({
        id: entry?.id,
        action: entry?.action,
        description: entry?.description,
        user: entry?.user_profiles?.email || 'System',
        userName: entry?.user_profiles?.full_name || 'System',
        metadata: entry?.metadata,
        timestamp: new Date(entry.created_at)
      }));
    } catch (error) {
      console.error('Error fetching audit trail:', error);
      throw error;
    }
  },

  /**
   * Create audit trail entry
   * @param {string} action - Action type
   * @param {string} description - Action description
   * @param {Object} metadata - Additional metadata
   * @returns {Promise<Object>} Created entry
   */
  async createAuditEntry(action, description, metadata = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('configuration_audit_trail')?.insert({
          action,
          description,
          user_id: user?.id,
          metadata
        })?.select(`
          id,
          action,
          description,
          user_id,
          metadata,
          created_at,
          user_profiles (
            email,
            full_name
          )
        `)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        action: data?.action,
        description: data?.description,
        user: data?.user_profiles?.email || 'System',
        userName: data?.user_profiles?.full_name || 'System',
        metadata: data?.metadata,
        timestamp: new Date(data.created_at)
      };
    } catch (error) {
      console.error('Error creating audit entry:', error);
      throw error;
    }
  },

  /**
   * Subscribe to real-time system status updates
   * @param {Function} callback - Callback function for updates
   * @returns {Object} Subscription object
   */
  subscribeToSystemStatus(callback) {
    return supabase?.channel('system_status_changes')?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'system_status'
        },
        (payload) => {
          const data = payload?.new;
          callback({
            status: data?.status,
            activeUsers: data?.active_users,
            databaseSize: `${(data?.database_size_mb / 1024)?.toFixed(1)} GB`,
            storageUsed: `${(data?.storage_used_mb / 1024)?.toFixed(1)} GB`,
            uptime: `${data?.uptime_percentage}%`,
            lastUpdated: new Date(data.last_checked_at)
          });
        }
      )?.subscribe();
  },

  /**
   * Subscribe to real-time audit trail updates
   * @param {Function} callback - Callback function for new entries
   * @returns {Object} Subscription object
   */
  subscribeToAuditTrail(callback) {
    return supabase?.channel('audit_trail_changes')?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'configuration_audit_trail'
        },
        async (payload) => {
          // Fetch user profile for the new entry
          const { data: userProfile } = await supabase?.from('user_profiles')?.select('email, full_name')?.eq('id', payload?.new?.user_id)?.single();

          callback({
            id: payload?.new?.id,
            action: payload?.new?.action,
            description: payload?.new?.description,
            user: userProfile?.email || 'System',
            userName: userProfile?.full_name || 'System',
            metadata: payload?.new?.metadata,
            timestamp: new Date(payload.new.created_at)
          });
        }
      )?.subscribe();
  }
};